import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { LogOut, CircleHelp } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "@/utils/toast.utils";
import { useTranslation } from "react-i18next";
import { MobileNavigation, MobileNavigationProps } from "./MobileNavigation";
import { MerchantLocation } from "../blocks/Header/MerchantLocation";
import { useIsPermitted } from "@/hooks/useIsPermitted";
import { ROLES } from "@/common/permissions";
import { captureException } from "@/utils/sentry";

interface HeaderProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  navigation: MobileNavigationProps["navigation"];
}

export function Header({ isOpen, setIsOpen, navigation }: HeaderProps) {
  const { signOut } = useAuth();
  const { t } = useTranslation();
  const isSupportPermitted = useIsPermitted(ROLES.MANAGER);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success(t("auth.signOutSuccess"));
    } catch (error) {
      console.error("Log out error:", error);
      captureException(error as Error, {
        extra: {
          component: 'Header',
          action: 'handleSignOut',
        },
      });
      toast.error(t("auth.signOutError"));
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-dark backdrop-blur supports-[backdrop-filter]:bg-dark/100">
      <div className="container px-0 flex h-14 max-w-screen-2xl items-center justify-between">
        <div className="flex items-center">
          <MobileNavigation
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            navigation={navigation}
          />
        </div>

        <div className="flex items-center justify-center space-x-2 flex-1 min-w-0">
          <MerchantLocation />
        </div>

        <div className="flex items-center shrink-0 gap-0">
          {isSupportPermitted ? (
            <Link to="/support">
              <Button variant="ghost" size="icon" aria-label="Help" className="-mr-2.5">
                <CircleHelp className="h-5 w-5" />
              </Button>
            </Link>
          ) : (
            <div 
              className="-mr-2.5 opacity-50 cursor-not-allowed flex h-10 w-10 items-center justify-center rounded-md text-sm font-medium transition-colors"
              aria-label="Help (not available)"
            >
              <CircleHelp className="h-5 w-5" />
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={handleSignOut}>
            <LogOut className="h-5 w-5" />
            <span className="sr-only">{t("auth.signOut")}</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
